import React from 'react';
import {
  ReferenceManyField,
  TextField,
  SimpleShowLayout,
  Datagrid,
  DeleteWithConfirmButton,
  FunctionField,
  Pagination,
  ArrayField,
  SingleFieldList,
  ChipField,
  useRecordContext,
} from 'react-admin';
import LinkField from 'components/LinkField';
import PolymorphicField from 'components/PolymorphicField';
import { ENUMS } from '../Enum/enums';
import Heading from '../Heading';
import MassBlockFavoriteDialog from 'components/MassBlockFavoriteDialog';
import DateTimeField from 'components/DateTimeField';

const RelationName = ({ record, source }: { record?: Record<string, any>; source: string }) => {
  return <>{record?.[source].name ?? record?.[source].display_name}</>;
};

const getRelationshipsTab = (requester, requestee) =>
  function RelationshipTab(props) {
    const record = useRecordContext();
    const search = requester === 'Location' ? { exclude_deactivated: true } : {};
    return (
      <SimpleShowLayout>
        {props.canCreate && <MassBlockFavoriteDialog requester={requester} requestee={requestee} />}
        <Heading>{`Blocked ${requestee}s`}</Heading>
        <ReferenceManyField
          target="requester_id"
          filter={{
            search,
            requester_type: requester,
            requestee_type: requestee,
            kind: ENUMS.Relationship.kind.block,
          }}
          perPage={10}
          pagination={<Pagination />}
          reference="Relationship"
          label=""
        >
          <Datagrid bulkActionButtons={false}>
            <FunctionField
              label="ID"
              render={(v) => <LinkField to={`/Relationship/${v.id}/show`}>{v.id}</LinkField>}
            />
            <FunctionField
              label="Name"
              render={(v) => <RelationName record={v} source="requestee" />}
            />
            <PolymorphicField source="requestee" />
            <TextField label="Reason" source="reason.label" />
            <TextField source="comments" />
            <DateTimeField source="created_at" />
            {props.canDelete && (
              <DeleteWithConfirmButton
                redirect={`/${requester}/${record?.id}/show/relationships`}
              />
            )}
          </Datagrid>
        </ReferenceManyField>

        <Heading>{`Blocked by ${requestee}s`}</Heading>
        <ReferenceManyField
          target="requestee_id"
          filter={{
            search,
            requestee_type: requester,
            requester_type: requestee,
            kind: ENUMS.Relationship.kind.block,
          }}
          perPage={10}
          pagination={<Pagination />}
          reference="Relationship"
          label=""
        >
          <Datagrid bulkActionButtons={false}>
            <FunctionField
              label="ID"
              render={(v) => <LinkField to={`/Relationship/${v.id}/show`}>{v.id}</LinkField>}
            />
            <FunctionField
              label="Name"
              render={(v) => <RelationName record={v} source="requester" />}
            />
            <PolymorphicField source="requester" />
            <TextField label="Reason" source="reason.label" />
            <TextField source="comments" />
            <DateTimeField source="created_at" />
            {props.canDelete && (
              <DeleteWithConfirmButton
                redirect={`/${requester}/${record?.id}/show/relationships`}
              />
            )}
          </Datagrid>
        </ReferenceManyField>

        {requester === 'Location' && (
          <>
            <Heading>{`Favorite ${requestee}s`}</Heading>
            <ReferenceManyField
              label=""
              target="requester_id"
              filter={{
                search,
                requester_type: requester,
                requestee_type: requestee,
                kind: ENUMS.Relationship.kind.favorite,
              }}
              perPage={10}
              pagination={<Pagination />}
              reference="Relationship"
            >
              <Datagrid bulkActionButtons={false}>
                <FunctionField
                  label="ID"
                  render={(v) => <LinkField to={`/Relationship/${v.id}/show`}>{v.id}</LinkField>}
                />
                <FunctionField
                  label="Name"
                  render={(v) => <RelationName record={v} source="requestee" />}
                />
                <PolymorphicField source="requestee" />
                <TextField label="Reason" source="reason.label" />
                <TextField source="comments" />
                <ArrayField label="Requestee Positions" source="requestee.positions">
                  <SingleFieldList linkType={false}>
                    <ChipField source="display_name" />
                  </SingleFieldList>
                </ArrayField>
                <DateTimeField source="created_at" />
                {props.canDelete && (
                  <DeleteWithConfirmButton
                    redirect={`/${requester}/${record?.id}/show/relationships`}
                  />
                )}
              </Datagrid>
            </ReferenceManyField>
          </>
        )}

        {requester === 'Professional' && (
          <>
            <Heading>{`Favored by ${requestee}s`}</Heading>
            <ReferenceManyField
              label=""
              target="requestee_id"
              filter={{
                search,
                requestee_type: requester,
                requester_type: requestee,
                kind: ENUMS.Relationship.kind.favorite,
              }}
              perPage={10}
              pagination={<Pagination />}
              reference="Relationship"
            >
              <Datagrid
                bulkActionButtons={false}
                data-testid="professional_show-relationships_tab_favored_table"
              >
                <FunctionField
                  label="ID"
                  render={(v) => <LinkField to={`/Relationship/${v.id}/show`}>{v.id}</LinkField>}
                />
                <FunctionField
                  label="Name"
                  render={(v) => <RelationName record={v} source="requester" />}
                  data-testid="professional_show-relationships_tab_favored_table_name"
                />
                <PolymorphicField source="requester" />
                <TextField
                  label="Reason"
                  source="reason.label"
                  data-testid="professional_show-relationships_tab_favored_table_reason"
                />
                <TextField
                  source="comments"
                  data-testid="professional_show-relationships_tab_favored_table_comments"
                />
                <DateTimeField source="created_at" />
                {props.canDelete && (
                  <DeleteWithConfirmButton
                    redirect={`/${requester}/${record?.id}/show/relationships`}
                    data-testid="professional_show-relationships_tab_favored_table_delete_button"
                  />
                )}
              </Datagrid>
            </ReferenceManyField>
          </>
        )}

        {requester === 'Professional' && (
          <>
            <Heading>Favorites Location</Heading>
            <ReferenceManyField
              target="requester_id"
              filter={{
                search,
                requestee_type: requestee,
                requester_type: requester,
                kind: ENUMS.Relationship.kind.favorite,
              }}
              perPage={10}
              pagination={<Pagination />}
              reference="Relationship"
            >
              <Datagrid bulkActionButtons={false}>
                <FunctionField
                  label="ID"
                  render={(v) => <LinkField to={`/Relationship/${v.id}/show`}>{v.id}</LinkField>}
                />
                <FunctionField
                  label="Name"
                  render={(v) => <RelationName record={v} source="requestee" />}
                />
                <PolymorphicField source="requestee" />
                <TextField label="Reason" source="reason.label" />
                <TextField source="comments" />
                <DateTimeField source="created_at" />
                {props.canDelete && (
                  <DeleteWithConfirmButton
                    redirect={`/${requester}/${record?.id}/show/relationships`}
                  />
                )}
              </Datagrid>
            </ReferenceManyField>
          </>
        )}
      </SimpleShowLayout>
    );
  };

export default getRelationshipsTab;
